import React, { useCallback, useMemo, useState, JSX } from 'react';
import mapEurope from '../data/mapEurope.json';
import mapSpain from '../data/mapSpain.json';
import mapGermany from '../data/mapGermany.json';
import mapItaly from '../data/mapItaly.json';
import mapAustria from '../data/mapAustria.json';
import { Picturable } from './Picturable';
import { useNavigate } from 'react-router-dom';
import { getAllCountries, getCountryData, getGroup, getIndexOfMax, getPrediction, getPredictionSeats, getShort2, withParty, withPartyOnPolls } from '../calculations/utilsV2';
import { fixCountryStats, getColorByGroup, getColorByGroupScore, getColorByLeftRightScore, getColorByLibConScore, getGrayColorByScore, getLightColorByGroup, GROUPS, leftRightScore, libConScore } from '../calculations/calcV2';

interface EuropeMapProps {
    countryStats: CountryStat[],
}

export const EuropeMapV2 = (props: EuropeMapProps) => {
    const { countryStats } = props;
    const allCountries = getAllCountries();
    fixCountryStats(countryStats, allCountries);

    const [type, setType] = useState(0);
    const titles = ["the biggest group in a country", "prime minister party", "left/right score", "liberal/conservative score", "the biggest gain", "EU seats", 
        "government popularity", "latest poll", "next election", "",
        "Left", "Green", "S&D", "RE", "EPP", "ECR", "PfE", "ESN", "NI"];

    const getCountryStat = (country: string) => countryStats.find(x => x.Country === country && !x.Region);
    const countries = countryStats.filter(x => x.DataType === 'country');
    const predictions = countries.map(x => getPrediction(x));
    const getPredictionPerCountry = (country: string) => predictions[countries.map(x => x.Country).indexOf(country)];

    const getChangeSinceElection = (country: string): PartyValue[] | undefined => {
        const prediction = getPredictionPerCountry(country);
        const countryStat = getCountryStat(country);
        const elections = countryStat ? withParty(countryStat, countryStat.Elections[0]) : null;
        return prediction?.map(x => {
                const value = elections?.find(y => y.party === x.party)?.value;
                return ( { ...x, value: (x.value - (value !== 0 && value ? value : x.value)) }); })
            .filter(x => x.value > 0);
    }
    const getDataChange = (country: string): number[] | undefined => {
        const change = getChangeSinceElection(country);
        //console.log('change', country, change);
        return GROUPS.map(group => change?.filter(x => x.group === group).reduce((s, a) => a.value + s, 0) ?? 0);
    }
    const dataChange = (country: string, filterLow: boolean = true): string | null => {
        const change = getDataChange(country);
        if (!change || (filterLow && Math.max(...change) < 4)) { return null; }
        return GROUPS[getIndexOfMax(change)];
    }
    const dataPredict = (country: string): string => {
        const prediction = getPredictionPerCountry(country);
        const maxGroup = prediction ? GROUPS[getIndexOfMax(GROUPS.map(g => prediction.filter(x => x.group === g).reduce((s, a) => a.value + s, 0)))]: ''
        return maxGroup;
    }
    const dataGovPerc = (country: string): number => {
        const prediction = getPredictionPerCountry(country);
        if (!country || !prediction) { return 0; }
        const countryStat = getCountryStat(country);
        if (!countryStat) { return 0; }
        const countryData = getCountryData(countryStat);
        const predictionSeats = getPredictionSeats(countryStat, prediction);
        const govSeats = predictionSeats.filter(x => countryStat.Governments[0].Parties.includes(x.party)).reduce((s, a) => a.value + s, 0);
        const perc = govSeats / (countryData.ParliamentSeats ?? 1);
        return Math.round(100 * perc);
    }
    const predict = (country: string): number => {
        const prediction = getPredictionPerCountry(country);
        const countryStat = getCountryStat(country);
        if (!countryStat) { return 0; }
        const group = GROUPS[type - 10];
        return prediction?.filter(x => x.group === group).reduce((s, a) => Math.round(10 * (a.value + s))/10, 0) ?? 0;
    }
    const pollAge = (country: string): number | null => {
        const countryStat = getCountryStat(country);
        if (countryStat && countryStat.Polls.length > 0) {
            const date = countryStat.Polls[0].DateTo;
            return Math.floor((new Date().getTime() - new Date(date).getTime())/((1000 * 60 * 60 * 24)));
        }
        return null;
    }
    const primeMinisterGroup = (country: string): string | null => {
        const countryStat = getCountryStat(country);
        if (countryStat && countryStat.Governments.length > 0 && countryStat.Governments[0].Parties && countryStat.Governments[0].Parties.length > 0) {
            const pmParty = countryStat.Governments[0].Parties[0];
            const group = getGroup(countryStat, pmParty);
            return group;
        }
        return null;
    }
    const leftRightText = (country: string): string | null => {
        const value = leftRight(country);
        if (value) {
            if (value > 0) { return `${value}% (left)`; }
            if (value < 0) { return `${-1 * value}% (right)`; }
        }
        return null;
    }
    const leftRight = (country: string): number => {
        const prediction = getPredictionPerCountry(country);
        if (!prediction) { return 0; }
        return leftRightScore(prediction);
    }
    const libConText = (country: string) => {
        const value = libCon(country);
        if (value) {
            if (value > 0) { return `${value}% (liberal)`; }
            if (value < 0) { return `${-1 * value}% (conservative)`; }
        }
        return null;
    }
    const libCon = (country: string): number => {
        const prediction = getPredictionPerCountry(country);
        if (!prediction) { return 0; }
        return libConScore(prediction);
    }
    const seats = (country: string): number => {
        const countryStat = getCountryStat(country);
        return countryStat ? getCountryData(countryStat).EuSeats ?? 0 : 0;
    }
    const nextElection = (country: string): number => {
        const countryStat = getCountryStat(country);
        if (countryStat) {
            const countryData = getCountryData(countryStat);
            const nextElectionYear = countryStat.Elections[0].Year + countryData.ElectionCycle;
            return nextElectionYear;
        }
        return 0;
    }

    const getDataExt = (country: string): string => {
        const countryStat = getCountryStat(country);
        return (countryStat ?
            (type === 0 
                ? `${dataPredict(country)} (${Math.round(Math.max(...getPredictionPerCountry(country)?.map(x => x.value) ?? [0]))}%)`
                : type === 4 ? `${getData(country, false)} (${Math.round(Math.max(...getDataChange(country) ?? [0]))}%)`
                : type === 6 ? `${getData(country)}%`
                : type === 7 ? `${getData(country)} days`
                : type === 8 ? `${getData(country)}`
                : type < 10 ? getData(country)?.toString() : `${getData(country)}%`)
                : '') ?? '';
            }
    const getData = (country: string, filterLow: boolean = true) => 
        type === 0 ? dataPredict(country) 
        : (type === 1 ? primeMinisterGroup(country)
        : (type === 2 ? leftRightText(country)
        : (type === 3 ? libConText(country)
        : (type === 4 ? dataChange(country, filterLow)
        : (type === 5 ? seats(country)
        : (type === 6 ? dataGovPerc(country)
        : (type === 7 ? pollAge(country)
        : (type === 8 ? nextElection(country)
        : predict(country)))))))));
    const getColor = (country: string, isOverSame: boolean, isDefault: boolean) => {
        if (type === 0) {
            return getColorByFunc(() => dataPredict(country), isOverSame);
        } else if (type === 1) {
            return getColorByFunc(() => primeMinisterGroup(country), isOverSame);
        } else if (type === 2) {
            return getLeftRightColorByFunc(() => leftRight(country), isOverSame);
        } else if (type === 3) {
            return getLibConColorByFunc(() => libCon(country), isOverSame);
        } else if (type === 4) {
            return getColorByFunc(() => dataChange(country, true), isOverSame);
        } else if (type === 5) {
            return getSeatsColorByFunc(() => seats(country), isOverSame, isDefault);
        } else if (type === 6) {
            return getSeatsColorByFunc(() => Math.max(0, Math.min(100, 3 * (dataGovPerc(country) ?? 0) - 60)), isOverSame, isDefault);
        } else if (type === 7) {
            const age = pollAge(country) ?? 0;
            return getSeatsColorByFunc(() => age ? 2*(50 - Math.pow(age, 0.4)*5) : age, isOverSame, isDefault);
        } else if (type === 8) {
            const currYear = new Date().getFullYear();
            const next = nextElection(country);
            return getSeatsColorByFunc(() => next ? (100 * (- next + currYear + 6) / 6) : 0, isOverSame, isDefault);
        } else {
            return getColorGroupScoreByFunc(() => predict(country), isOverSame);
        }
    }
    const getColorByFunc = (getGroup: () => string | null, isSame: boolean): string => {
        const group = getGroup();
        return group ? (isSame ? getLightColorByGroup(group) : getColorByGroup(group))
            : (isSame ? '#ccc' : 'transparent');
    }
    const getLeftRightColorByFunc = (getScore, isSame) => {
        const score = getScore();
        return score ? getColorByLeftRightScore(score)
            : (isSame ? '#ccc' : 'transparent');
    }
    const getLibConColorByFunc = (getScore, isSame) => {
        const score = getScore();
        return score ? getColorByLibConScore(score)
            : (isSame ? '#ccc' : 'transparent');
    }
    const getSeatsColorByFunc = (getScore: () => number, isSame: boolean, isDefault: boolean): string => {
        const def = (isDefault ? 'transparent' : '#333');
        const score = getScore();
        return (score ? getGrayColorByScore(score)
            : (isSame ? '#ccc' : def)) ?? def;
    }
    const getColorGroupScoreByFunc = (getScore: () => number, isSame: boolean) => {
        const def = (isSame ? '#ccc' : 'transparent');
        const score = getScore();
        return (score ? getColorByGroupScore(GROUPS[type-10], score)
            : def) ?? def;
    }

    return <div>
            <GeneralMapV2 country='Europe' type={type} setType={setType} titles={titles} title={titles[type]}
                funcOverText={(country, cal) => <>{country}{cal ? ':' : ''} {cal}</>}
                funcGetColor={(country, isOverSame, isDefault) => getColor(country, isOverSame, isDefault)}
                funcCalculate={(country) => getDataExt(country)} />
            <div className="margin12">
                <span className="highlight pointer hoverable2 groupLeft" onClick={() => setType(10)}>Left</span>
                <span className="highlight pointer hoverable2 groupG" onClick={() => setType(11)}>Green</span>
                <span className="highlight pointer hoverable2 groupSD" onClick={() => setType(12)}>S&D</span>
                <span className="highlight pointer hoverable2 groupRe" onClick={() => setType(13)}>RE</span>
                <span className="highlight pointer hoverable2 groupEpp" onClick={() => setType(14)}>EPP</span>
                <span className="highlight pointer hoverable2 groupEcr" onClick={() => setType(15)}>ECR</span>
                <span className="highlight pointer hoverable2 groupId" onClick={() => setType(16)}>PfE</span>
                <span className="highlight pointer hoverable2 groupEsn" onClick={() => setType(17)}>ESN</span>
                <span className="highlight pointer hoverable2 groupNi" onClick={() => setType(18)}>NI</span>
            </div>
    </div>
}

interface CountryMapProps {
    countryStats: CountryStat[],
    countryStat: CountryStat,
}

export const CountryMapV2 = (props: CountryMapProps) => {
    const { countryStats, countryStat } = props;
    const [type, setType] = useState(0);
    if (type !== 0 && countryStat.Country !== 'Germany'){
        setType(0);
    }

    const primeMinisterGroup = (region: string): string | null => {
        const regionStat = countryStats.find(x => x.Region === region);
        if (regionStat && regionStat.Governments.length > 0 && regionStat.Governments[0].Parties && regionStat.Governments[0].Parties.length > 0) {
            const pmParty = regionStat.Governments[0].Parties[0];
            const group = getGroup(countryStat, pmParty);
            return group;
        }
        return null;
    }
    const biggestPartyData = (region: string): PartyValue => {
        const regionStat = countryStats.find(x => x.Region === region);
        if (regionStat && regionStat.Polls.length > 0) {
            const partiesPoll = regionStat.Polls[0];
            const valueParty = withPartyOnPolls(regionStat, partiesPoll);
            const maxIndex = getIndexOfMax(valueParty.map(x => x.value));
            return valueParty[maxIndex];
        }
        return { value: 0, party: '', group: '' };
    }
    const pollAge = (region: string): number | null => {
        const regionStat = countryStats.find(x => x.Region === region);
        if (regionStat && regionStat.Polls.length > 0) {
            const poll = regionStat.Polls[0].DateTo;
            return Math.floor((new Date().getTime() - new Date(poll).getTime())/((1000 * 60 * 60 * 24)));
        }
        return null;
    }
    const populationData = (region: string) => {
        const regionStat = countryStats.find(x => x.Region === region);
        const regionData = regionStat ? getCountryData(regionStat) : null;
        if (regionData) {
            return Math.floor((regionData.Population ?? 0) / 100000)/10;
        }
        return null;
    }
    const getGroupScore = (region: string): number | null => {
        const regionStat = countryStats.find(x => x.Region === region);
        //console.log('regionStat', regionStat);
        if (regionStat && regionStat.Polls.length > 0) {
            const data = withPartyOnPolls(regionStat, regionStat.Polls[0]);

            const score = data.map(x => x.group === GROUPS[type-10] ? x.value : 0).reduce((s, a) => a + s, 0);
            return score;
        }
        return null;
    }

    const getDataExt = (region: string): string => {
        const f1 = (p: PartyValue): string => p.party ? `${p.party} (${p.group}) (${p.value})` : '';
        const f2 = (p: number | null): string => p ? p + ' days' : '';
        const f3 = (p: number | null): string => p ? p + ' millions' : '';
        const f4 = (p: number | null): string => !p || p === 0 ? '' : p.toString();

        const result = type === 0 
            ? primeMinisterGroup(region)
            : type === 1 ? f1(biggestPartyData(region))
            : type === 2 ? f2(pollAge(region)) 
            : type === 3 ? f3(populationData(region))
            : f4(getGroupScore(region));
        return result ?? '';
    }
    
    const getColor = (reg: string, isSame: boolean): string => {
        const def = (isSame ? '#ccc' : 'transparent');
        if (type >= 10) {
            const score = getGroupScore(reg);
            return (score ? getColorByGroupScore(GROUPS[type-10], score)
            : def) ?? def;
        } else {
            const f0 = (p: string | null) => p ? isSame ? getLightColorByGroup(p) : getColorByGroup(p) : 'transparent';
            const f2 = (p: number | null) => p ? getGrayColorByScore(100 - p) : 'transparent';
            const f3 = (p: number | null) => p ? getGrayColorByScore(10 * p) : 'transparent'; // 10 stands for 10 million

            const group = type === 0 ? f0(primeMinisterGroup(reg))
                : type === 1 ? f0(biggestPartyData(reg).group)
                : type === 2 ? f2(pollAge(reg))
                : f3(populationData(reg));
            return group ? group : def;
        }
    }
    
    const titles = ["regional government", "", "", "", "", "", "", "", "", "", "Left", "Green", "S&D", "RE", "EPP", "ECR", "PfE", "ESN", "NI"];
    switch (countryStat.Country) {
        case "Spain": break;
        case "Germany": titles[1] = "polls - biggest party"; titles[2] = "latest poll"; titles[3] = "population"; break;
        case "Italy": break;
        case "Austria": break;
        default: break;
    }

    return <><GeneralMapV2 country={countryStat.Country} titles={titles} type={type} setType={setType} title={titles[type]}
        funcOverText={(reg, cal) => <>{reg}{cal ? ':' : ''} {cal}</>} 
        funcGetColor={(reg, isOverSame, isDefault) => getColor(reg, isOverSame)} 
        funcCalculate={(reg) => getDataExt(reg)} />
        { countryStat.Country === "Germany" &&
        <div className="margin12">
            <span className="highlight pointer hoverable2 groupLeft" onClick={() => setType(10)}>Left</span>
            <span className="highlight pointer hoverable2 groupG" onClick={() => setType(11)}>Green</span>
            <span className="highlight pointer hoverable2 groupSD" onClick={() => setType(12)}>S&D</span>
            <span className="highlight pointer hoverable2 groupRe" onClick={() => setType(13)}>RE</span>
            <span className="highlight pointer hoverable2 groupEpp" onClick={() => setType(14)}>EPP</span>
            <span className="highlight pointer hoverable2 groupEcr" onClick={() => setType(15)}>ECR</span>
            <span className="highlight pointer hoverable2 groupId" onClick={() => setType(16)}>PfE</span>
            <span className="highlight pointer hoverable2 groupEsn" onClick={() => setType(17)}>ESN</span>
            <span className="highlight pointer hoverable2 groupNi" onClick={() => setType(18)}>NI</span>
        </div>
        }
        </>
}

interface GeneralMapProps {
    type: number,
    setType: (i: number) => void;
    titles: string[],
    title: string,
    funcOverText: (overCountry: string, calculated: string) => JSX.Element;
    funcGetColor: (name: string, isOverSame: boolean, isDefault: boolean) => string;
    funcCalculate: (overCountry: string) => string;
    country: string,
    //map: { name: string; paths: string[]; }[],
}

export const GeneralMapV2 = (props: GeneralMapProps) => {
    const { type, setType, titles, title, funcOverText, funcGetColor, funcCalculate, country } = props;
    const [overCountry, setOverCountry] = useState('');
    const calculated = useMemo(() => funcCalculate(overCountry), [overCountry, type]);
    const navigate = useNavigate();
    const titleNew = country + " - " + title;

    let map: { name: string; paths: string[]; }[];
    let move = 0;
    let moveY = 0;
    let aspect = 1;
    let onClick: () => void;
    switch (country) {
        case "Spain": map = mapSpain; move = 20; aspect = 1; onClick=() => {}; break;
        case "Germany": map = mapGermany; move = 160; aspect = 0.62; onClick=() => { navigate("/region/" + overCountry); }; break;
        case "Italy": map = mapItaly; move = 0; aspect = 1; moveY = 30; onClick=() => {}; break;
        case "Austria": map = mapAustria; move = 0; aspect = 1; moveY = 30; onClick=() => {}; break;
        default: map = mapEurope; move = 0; aspect = 1; onClick=() => { 
            const short = getShort2(overCountry);
            if (short){ navigate("/" + short); }
        }; 
        break;
    }

    const items = useMemo(() => { return map.map(x => ({ ...x, color: funcGetColor(x.name, false, false), colorOver: funcGetColor(x.name, true, false) }))}, [type, country]);
    const text = useMemo(() => { return funcOverText(overCountry, calculated)}, [overCountry, type]);
    const getChildren = useCallback((isDefault: boolean) => {

        return (<div className="margin8">
            <img className={"absolute floatLeft" + (isDefault ? " collapsed" : "")} src="Statostuff.png" alt="Statostuff" />
            <div>
                <span className={"underline " + (isDefault ? "light" : "collapsed")}>{title ?? titleNew}</span>
                <div className="mapTable">
                    { titles && titles.filter((x, i) => i < 10 && x !== "").length > 1 &&
                        <div className="mapMenuPart">
                            { titles.filter((x, i) => x && i < 10).map((x, i) => {
                                    return <div key={i} className={"underline pointer " + (isDefault ? "light" : "dark")} onClick={() => setType(i)}>
                                        <span className={"dot " + (type === i ? "groupG" : "groupNi")} />&nbsp;{x}
                                    </div>
                                })
                            }
                        </div>
                    }

                    <div className="absolute margin12"><b>{text}</b></div>
                    <div className="margin12 mapPart">
                        <svg width="700" height="500" viewBox="0 0 600 500">
                            
    {/*<defs>
      <filter id="demo1">
        <feGaussianBlur stdDeviation="6" result="blur1" />
        <feSpecularLighting result="spec1" in="blur1" specularExponent="30" lighting-color="#999">
            <fePointLight x="150" y="120" z="200" />
        </feSpecularLighting>
        <feComposite in="SourceGraphic" in2="spec1" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" />
      </filter>
    </defs>*/}
    {/*<defs>
    <filter id="sofGlow" height="300%" width="300%" x="-75%" y="-75%">
        <feMorphology operator="dilate" radius="4" in="SourceAlpha" result="thicken" />
        <feGaussianBlur in="thicken" stdDeviation="5" result="blurred" />
        <feFlood flood-color="rgb(200,200,200)" result="glowColor" />
        <feComposite in="glowColor" in2="blurred" operator="in" result="softGlow_colored" />
        <feMerge>
            <feMergeNode in="softGlow_colored"/>
            <feMergeNode in="SourceGraphic"/>
        </feMerge>
    </filter>
    </defs>*/}
    {/* https://www.fffuel.co/nnneon/ */}
    {<defs>
        <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="nnneon-grad">
            <stop stopColor="hsl(157, 100%, 54%)" stopOpacity="1" offset="0%"></stop>
            <stop stopColor="hsl(331, 87%, 61%)" stopOpacity="1" offset="100%"></stop>
        </linearGradient>
        <filter id="nnneonFilter" x="-100%" y="-100%" width="400%" height="400%" filterUnits="objectBoundingBox" primitiveUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feGaussianBlur stdDeviation="33 11" x="-50%" y="0%" width="200%" height="200%" in="SourceGraphic" edgeMode="none" result="blur"></feGaussianBlur></filter>
        <filter id="nnneonFilter2" x="-100%" y="-100%" width="400%" height="400%" filterUnits="objectBoundingBox" primitiveUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feGaussianBlur stdDeviation="51 22" x="-50%" y="0%" width="200%" height="200%" in="SourceGraphic" edgeMode="none" result="blur"></feGaussianBlur></filter>
    </defs>}

                            <g filter="url(#nnneonFilter2)" /*filter="url(#sofGlow)" filter="url(#demo1)"*/ stroke="black" fill="transparent" transform={`translate(${move}, ${moveY}) matrix(${aspect},0,0,${aspect},0,0)`}>
                                {
                                    items.map((x, i) => { 
                                        return <g key={i} className="pointer" fill={x.name === "x" ? "red" : overCountry === x.name ? x.colorOver : x.color}
                                            opacity={overCountry === x.name ? 0.8 : 1} 
                                            onMouseLeave={() => setOverCountry('')}
                                            onMouseOver={() => setOverCountry(x.name)}>
                                                {x.paths.map((y, j) => <path key={j} d={y} />)}
                                        </g> 
                                    })
                                }
                            </g>
                            <g stroke="black" fill="transparent" transform={`translate(${move}, ${moveY}) matrix(${aspect},0,0,${aspect},0,0)`}>
                                {
                                    items.map((x, i) => { 
                                        return <g key={i} className="pointer" fill={x.name === "x" ? "red" : overCountry === x.name ? x.colorOver : x.color}
                                            opacity={overCountry === x.name ? 0.8 : 1}
                                            onClick={() => onClick()}
                                            onMouseLeave={() => setOverCountry('')}
                                            onMouseOver={() => setOverCountry(x.name)}>
                                                {x.paths.map((y, j) => <path key={j} d={y} />)}
                                        </g> 
                                    })
                                }
                            </g>
                        </svg>
                    </div>
                </div>
            </div>
        </div>)
    }, [overCountry, type]);

    return <Picturable getChildren={getChildren} downloadName={`map_${country ?? 'EU'}_${type ?? 'default'}.png`} />
}